<template>
  <div id="dettached-checkin">
    <b-container>
      <b-row v-if="!key">
        <b-col>
          <div class="text-center my-3">
            <h3>{{ $t("Checkin not available") }}</h3>
            <p>{{ $t("The provided key is not valid for this booking") }}</p>
          </div>
        </b-col>
      </b-row>

      <div v-else>
        <!-- Logo section -->
        <b-row class="mb-4 d-flex justify-content-center">
          <b-col cols="12" md="8" lg="6" class="text-center">
            <img src="/foravila-rentals-logo.svg" alt="Foravila Rentals Logo" class="checkin-logo my-4" />
          </b-col>
        </b-row>

        <b-card class="checkin-card shadow-sm mb-5">
          <b-row class="mb-4">
            <b-col>
              <h2 class="checkin-title text-center">{{ $t("Online check-in") }}</h2>
            </b-col>
          </b-row>

          <b-card class="checkin-privacy-card border-primary mb-4">
            <div class="d-flex align-items-start">
              <feather-icon icon="ShieldIcon" size="20" class="text-primary mt-25 mr-2 flex-shrink-0" />
              <div>
                <h5 class="font-weight-bold mb-3">{{ $t('legal.checkinTabPrivacyTitle') }}</h5>
                <p class="mb-0">
                  {{ $t('legal.checkinDetachedPrivacyText') }}
                  <a href="https://www.foravilarentals.com/privacy-policy" target="_blank" rel="noopener noreferrer">{{ $t('legal.privacyPolicyLink') }}</a>.
                </p>
              </div>
            </div>
          </b-card>

          <b-overlay :show="loading" variant="white" :opacity="0.9">
            <validation-observer ref="guestFormRules">
              <b-form id="guest-form">
                <!-- GUEST INFORMATION -->
                <b-card class="section-card mb-4">
                  <h5 class="section-title mb-3">
                    {{ $t("Personal information") }}
                  </h5>
                  <b-row>
                    <!-- FIRST NAME -->
                    <b-col cols="12" md="6" class="mb-3">
                      <b-form-group :label="$t('First name')" label-for="guest-first-name">
                        <validation-provider #default="{ errors }" name="firstName" rules="required">
                          <b-form-input id="guest-first-name" v-model="newGuest.firstName"
                            :state="errors.length > 0 ? false : null" trim />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <!-- LAST NAME -->
                    <b-col cols="12" md="6" class="mb-3">
                      <b-form-group :label="$t('Last name')" label-for="guest-last-name">
                        <validation-provider #default="{ errors }" name="lastName"
                          :rules="newGuest.idType === 'NIF' ? 'required|twolastnames' : 'required'">
                          <b-form-input id="guest-last-name" v-model="newGuest.lastName"
                            :state="errors.length > 0 ? false : null" trim />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <!-- BIRTHDATE -->
                    <b-col cols="12" class="mb-2">
                      <label for="guest-birthday">
                        {{ $t("Birthdate") }}
                      </label>
                      <validation-provider #default="{ errors }" name="birthdate" rules="required|pastdate">
                        <b-datepicker v-model="newGuest.birthDate" :max="new Date()"
                          :state="errors.length > 0 ? false : null" class="responsive-datepicker" />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </b-card>

                <!-- ADDRESS INFORMATION -->
                <b-card class="section-card mb-4">
                  <h5 class="section-title mb-3">
                    {{ $t("Address information") }}
                  </h5>
                  <b-row>
                    <!-- ADDRESS COUNTRY -->
                    <b-col cols="12" class="mb-3">
                      <b-form-group :label="$t('Country')" label-for="guest-address-country">
                        <validation-provider #default="{ errors }" name="country" rules="required">
                          <v-select id="guest-address-country" v-model="newGuest.addressCountry"
                            :options="countryOptionsAlpha3" :reduce="option => option.value" class="w-full"
                            :class="{ 'is-invalid': errors.length > 0 }" />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <!-- ADDRESS ZIP SELECT -->
                    <b-col v-if="newGuest.addressCountry === 'ESP'" cols="12" md="6" class="mb-3">
                      <b-form-group :label="$t('Postal code')" label-for="guest-address-zip-select">
                        <validation-provider #default="{ errors }" name="postalCode"
                          :rules="newGuest.addressCountry === 'ESP' ? 'required' : ''">
                          <v-select id="guest-address-zip-select" v-model="newGuest.addressZipInput" label="zipCode"
                            :filterable="false" :options="spainCityZipOptions" :disabled="!newGuest.addressCountry"
                            class="w-full" :class="{ 'is-invalid': errors.length > 0 }" @search="onSearchCityZip"
                            @input="autoFillCityInfo">
                            <template slot="no-options">
                              <span class="text-muted">{{ $t('Postal code search placeholder') }}</span>
                            </template>
                            <template slot="option" slot-scope="option">
                              <div>
                                <span>{{ option.zipCode }}</span>
                                <span class="ml-1">{{ option.cityName }}</span>
                              </div>
                            </template>
                          </v-select>
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <!-- ADDRESS ZIP TEXT INPUT -->
                    <b-col v-else cols="12" md="6" class="mb-3">
                      <b-form-group :label="$t('Postal code')" label-for="guest-address-zip">
                        <validation-provider #default="{ errors }" name="postalCode"
                          :rules="newGuest.addressCountry !== 'ESP' ? 'required' : ''">
                          <b-form-input id="guest-address-zip" v-model="newGuest.addressZip" trim
                            :state="errors.length > 0 ? false : null" />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <!-- ADDRESS CITY -->
                    <b-col cols="12" md="6" class="mb-3">
                      <b-form-group :label="$t('City')" label-for="guest-address-city">
                        <validation-provider #default="{ errors }" name="city" rules="required">
                          <b-form-input id="guest-address-city" v-model="newGuest.addressCityName" trim
                            :disabled="!newGuest.addressZip" :state="errors.length > 0 ? false : null" />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <!-- ADDRESS NAME -->
                    <b-col cols="12" class="mb-2">
                      <b-form-group :label="$t('Address')" label-for="guest-address">
                        <validation-provider #default="{ errors }" name="address" rules="required">
                          <b-form-input id="guest-address" v-model="newGuest.address" trim
                            :disabled="!newGuest.addressZip" :state="errors.length > 0 ? false : null" />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card>

                <!-- ID INFORMATION -->
                <b-card v-if="askForIdInformation" class="section-card mb-4">
                  <h5 class="section-title mb-3">
                    {{ $t("Identification") }}
                  </h5>
                  <b-row>
                    <!-- ID TYPE -->
                    <b-col cols="12" md="6" class="mb-3">
                      <label for="guest-id-type">
                        {{ $t("ID type") }}
                      </label>
                      <b-form-group>
                        <validation-provider #default="{ errors }" name="idType"
                          :rules="askForIdInformation ? 'required' : ''">
                          <v-select id="guest-country" v-model="newGuest.idType" :options="idTypeOptions"
                            :reduce="option => option.value" class="w-full" :class="{ 'is-invalid': errors.length > 0 }"
                            :disabled="!askForIdInformation" />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <!-- ID NUMBER -->
                    <b-col cols="12" md="6" class="mb-3">
                      <b-form-group :label="$t('ID number')" label-for="guest-id-number">
                        <validation-provider #default="{ errors }" name="idNumber" :rules="idNumberValidationRules">
                          <b-form-input id="guest-id-number" v-model="newGuest.idNumber" trim maxlength="15"
                            :formatter="value => value ? value.toUpperCase() : ''"
                            :state="errors.length > 0 ? false : null" :disabled="!askForIdInformation" />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <!-- ID SUPPORT NUMBER -->
                    <b-col v-if="newGuest.idType === 'NIF' || newGuest.idType === 'NIE'" cols="12" md="6" offset-md="6"
                      class="mb-2">
                      <b-form-group :label="$t('ID support number')" label-for="guest-id-support-number">
                        <validation-provider #default="{ errors }" name="idSupportNumber"
                          :rules="idSupportNumberValidationRules">
                          <b-form-input id="guest-id-support-number" v-model="newGuest.idSupportNumber" trim
                            :formatter="value => value ? value.toUpperCase() : ''"
                            :state="errors.length > 0 ? false : null" />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-card>

                <!-- SIGNATURE -->
                <b-card v-if="askForIdInformation" class="section-card mb-4">
                  <h5 class="section-title mb-3">
                    {{ $t("Signature") }}
                    <small class="cursor-pointer text-primary ml-25" @click="clearSignature()">
                      {{ $t("pages.booking.deleteSignature") }}
                    </small>
                  </h5>
                  <b-row>
                    <b-col cols="12" class="mb-3">
                      <p class="small text-muted mb-2">{{ $t('legal.signatureConsentText') }}</p>
                    </b-col>
                    <b-col v-if="newGuest.signature" cols="12" class="d-flex justify-content-center">
                      <b-img id="guest-signature" class="guest-signature mb-3" :src="newGuest.signature" alt="" fluid
                        center />
                    </b-col>
                    <b-col v-else cols="12">
                      <div class="signature-container">
                        <vue-signature-pad id="signature-pad" ref="signaturePad" width="100%" height="250px"
                          class="mb-3 cursor-pointer" :class="{ 'is-invalid': signaturePadError }"
                          :options="signaturePadOptions" />
                      </div>
                    </b-col>
                  </b-row>
                </b-card>

                <!-- PRIVACY CONSENT -->
                <b-card class="section-card mb-4">
                  <validation-provider #default="{ errors }" name="privacyConsent" rules="required">
                    <b-form-checkbox
                      id="privacy-consent"
                      v-model="privacyAccepted"
                      :value="true"
                      :unchecked-value="null"
                      :state="errors.length > 0 ? false : null"
                    >
                      <span class="small">
                        {{ $t('legal.checkinPrivacyNotice') }}
                        {{ $t('legal.checkinPrivacyConsent') }}
                        <a href="https://www.foravilarentals.com/privacy-policy" target="_blank" rel="noopener noreferrer">{{ $t('legal.privacyPolicyLink') }}</a>
                      </span>
                    </b-form-checkbox>
                    <small v-if="errors.length" class="text-danger">{{ $t('legal.mustAcceptPrivacy') }}</small>
                  </validation-provider>
                </b-card>

                <!-- BUTTONS -->
                <b-row class="mb-4">
                  <b-col cols="12">
                    <b-button block variant="primary" size="lg" class="checkin-submit-btn" @click="save()">
                      {{ $t("Complete Check-in") }}
                    </b-button>
                  </b-col>
                </b-row>
              </b-form>
            </validation-observer>
          </b-overlay>
        </b-card>
      </div>
    </b-container>
  </div>
</template>

<script>
import { BContainer, BRow, BCol, BOverlay, BForm, BFormGroup, BFormInput, BButton, BImg, BCard, BFormCheckbox } from "bootstrap-vue";
import vSelect from "vue-select";
import VueSignaturePad from "vue-signature-pad";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, pastdate, twolastnames, dni, nie, cif, soportedni, soportenie } from "@foravila-validations";
import BDatepicker from "@foravila-core/components/b-datepicker/BDatepicker.vue";
import { countryOptionsAlpha3, idTypeOptions } from "@/utils/select-options";
import axios from "@/axios";
import _debounce from "lodash/debounce";

export default {
  components: {
    BContainer,
    BRow,
    BCol,
    BOverlay,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BImg,
    BCard,
    BFormCheckbox,
    vSelect,
    VueSignaturePad,
    ValidationProvider,
    ValidationObserver,
    BDatepicker
  },
  data() {
    return {
      loading: false,
      privacyAccepted: null,
      newGuest: {
        firstName: null,
        lastName: null,
        birthDate: null,
        idType: null,
        idNumber: null,
        idSupportNumber: null,
        address: null,
        addressCountry: null,
        addressZip: null,
        addressZipInput: null,
        addressCityCode: null,
        addressCityName: null,
        signature: null,
      },
      signaturePadOptions: {
        penColor: "#000",
        onBegin: () => {
          if (this.$refs.signaturePad.isEmpty())
            this.$refs.signaturePad.resizeCanvas();
        }
      },
      signaturePadError: null,
      spainCityZipOptions: [],
      required,
      pastdate,
      twolastnames,
      dni, 
      nie, 
      cif, 
      soportedni,
      soportenie
    };
  },
  computed: {
    localizator() {
      return this.$route?.params?.localizator || null;
    },
    key() {
      return this.$route?.query?.key || null;
    },
    countryOptionsAlpha3() {
      return countryOptionsAlpha3(this.$i18n.locale);
    },
    idTypeOptions() {
      return idTypeOptions(this.$i18n.locale).filter(option => option.value !== "CIF" && option.value !== "DNI");
    },
    newGuestAge() {
      if (!this.newGuest.birthDate) return null;
      const birthDate = new Date(this.newGuest.birthDate);
      const ageDifMs = Date.now() - birthDate.getTime();
      const ageDate = new Date(ageDifMs);
      return Math.abs(ageDate.getUTCFullYear() - 1970);
    },
    askForIdInformation() {
      return this.newGuestAge >= 14;
    },
    idNumberValidationRules() {
      if (!this.askForIdInformation) {
        return "";
      }

      const rules = ["required", "max:15"];

      if (this.newGuest.idType === "NIF") {
        rules.push("dni")
      }

      if (this.newGuest.idType === "NIE") {
        rules.push("nie")
      }

      return rules.length ? rules.join("|") : "";
    },
    idSupportNumberValidationRules() {
      if (!this.askForIdInformation) {
        return "";
      }

      if (this.newGuest.idType === "NIF") {
        return "required|soportedni";
      }

      if (this.newGuest.idType === "NIE") {
        return "required|soportenie";
      }

      return "";
    }
  },
  methods: {
    clearSignature() {
      this.newGuest.signature = null;
      if (this.$refs.signaturePad) this.$refs.signaturePad.clearSignature();
      this.validateSignature();
    },
    save() {
      const validSignature = this.validateSignature();
      this.$refs.guestFormRules.validate().then(success => {
        if (success && validSignature) {
          this.createGuest();
        }
      });
    },
    createGuest() {
      try {
        this.loading = true;

        if (this.$refs.signaturePad) {
          const { data } = this.$refs.signaturePad.saveSignature();
          this.newGuest.signature = data || null;
        }

        // Delay to give time to the signature pad to process the data
        setTimeout(() => {
          axios
            .post(`/booking_dettached_guest`, {
              booking: this.localizator,
              key: this.key,
              guest: this.newGuest
            })
            .then(response => {
              if (response.status === 201) {
                this.$swal({
                  title: this.$t("pages.booking.dettachedCheckin.thanksModalTitle", { name: this.newGuest.firstName }),
                  text: this.$t("pages.booking.dettachedCheckin.thanksModalText"),
                  icon: "success",
                  showCancelButton: false,
                  confirmButtonText: this.$t("Close"),
                  customClass: {
                    confirmButton: "btn btn-success"
                  },
                  buttonsStyling: false
                });
                this.resetForm();
              } else {
                this.$swal({
                  title: this.$t("errors.default.title"),
                  text: this.$t("errors.default.text"),
                  icon: "error",
                  showCancelButton: false,
                  confirmButtonText: this.$t("Close"),
                  customClass: {
                    confirmButton: "btn btn-danger"
                  },
                  buttonsStyling: false
                });
              }
            })
            .catch(error => {
              const errorCode = error.response?.data?.code || null;
              const errorMessage = this.getErrorMessage(errorCode);

              this.$swal({
                title: errorMessage.title,
                text: errorMessage.text,
                icon: "error",
                showCancelButton: false,
                confirmButtonText: this.$t("Close"),
                customClass: {
                  confirmButton: "btn btn-danger"
                },
                buttonsStyling: false
              });
            })
            .finally(() => {
              this.loading = false;
            });
        }, 200);
      } catch (error) {
        this.loading = false;
      }
    },
    resetForm() {
      this.newGuest = {
        firstName: null,
        lastName: null,
        birthDate: null,
        idType: null,
        idNumber: null,
        idSupportNumber: null,
        address: null,
        addressCountry: null,
        addressZip: null,
        addressZipInput: null,
        addressCityCode: null,
        addressCityName: null,
        signature: null,
      };
      this.privacyAccepted = null;
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.clearSignature();
      }
      this.signaturePadError = null;
      this.spainCityZipOptions = [];
      this.$refs.guestFormRules.reset();
    },
    validateSignature() {
      if (!this.$refs.signaturePad) {
        this.signaturePadError = null;
        return true;
      }
      if (this.$refs.signaturePad.isEmpty()) {
        this.signaturePadError = true;
        return false;
      }
      this.signaturePadError = false;
      return true;
    },
    onSearchCityZip(search, loading) {
      if (search.length) {
        loading(true);
        this.fetchSpainCityZipOptions(search, loading, this);
      }
    },
    fetchSpainCityZipOptions: _debounce((search, loading, innerThis) => {
      axios
        .get(`/spain_zip_codes?zipCode=${search}`)
        .then(response => {
          const results = response.data["hydra:member"] || [];
          innerThis.spainCityZipOptions = results.sort((a, b) =>
            a.zipCode.localeCompare(b.zipCode)
          );
        })
        .catch(() => {
          innerThis.spainCityZipOptions = [];
        })
        .finally(() => loading(false));
    }, 1000),
    autoFillCityInfo(spainCity) {
      this.newGuest.addressZip = spainCity?.zipCode || null;
      this.newGuest.addressCityCode = spainCity?.cityCode || null;
      this.newGuest.addressCityName = spainCity?.cityName || null;
    },
    getErrorMessage(errorCode) {
      switch (errorCode) {
        case "BOOKING_NOT_FOUND":
          return {
            title: this.$t("errors.onlineCheckinBookingNotfound.title"),
            text: this.$t("errors.onlineCheckinBookingNotfound.text")
          };
        case "INVALID_KEY":
          return {
            title: this.$t("errors.onlineCheckinInvalidKey.title"),
            text: this.$t("errors.onlineCheckinInvalidKey.text")
          };
        case "CHECKIN_CLOSED":
          return {
            title: this.$t("errors.onlineCheckinCheckinClosed.title"),
            text: this.$t("errors.onlineCheckinCheckinClosed.text")
          };
        case "ACCOMMODATION_NOT_FOUND":
          return {
            title: this.$t("errors.onlineCheckinAccommodationNotFound.title"),
            text: this.$t("errors.onlineCheckinAccommodationNotFound.text")
          };
        case "MAX_CAPACITY_REACHED":
          return {
            title: this.$t("errors.onlineCheckinMaxCapacityReached.title"),
            text: this.$t("errors.onlineCheckinMaxCapacityReached.text")
          };
        default:
          return {
            title: this.$t("errors.default.title"),
            text: this.$t("errors.default.text")
          };
      }
    }
  }
};
</script>

<style lang="scss">
#dettached-checkin {
  padding: 15px;
  max-width: 1200px;
  margin: 0 auto;

  .vs__dropdown-toggle {
    padding: 0;
    height: 38px;
  }

  #guest-form {
    .guest-signature {
      max-width: 250px;
    }

    #signature-pad {
      border: solid 1px #d8d6de;
      background-origin: border-box;
      background-clip: content-box, border-box;

      &.is-invalid {
        border: solid 1px #ea5455;
      }
    }
  }

  .checkin-logo {
    max-width: 200px;
    height: auto;
    margin: 20px auto;
  }

  .checkin-card {
    padding: 20px;
    border-radius: 10px;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1) !important;
  }

  .checkin-title {
    font-size: 1.5rem;
    font-weight: bold;
  }

  .property-name {
    font-size: 1.25rem;
    color: #6c757d;
  }

  .section-card {
    padding: 15px;
    border: 1px solid #e9ecef;
    border-radius: 5px;
    margin-bottom: 1.5rem;
    transition: box-shadow 0.3s ease;

    &:hover {
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
    }
  }

  .section-title {
    font-size: 1.25rem;
    font-weight: bold;
    margin-bottom: 1rem;
  }

  .checkin-submit-btn {
    font-size: 1.15rem;
    padding: 12px;
    transition: transform 0.2s;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .signature-container {
    border: 1px solid #e9ecef;
    border-radius: 5px;
    padding: 10px;
  }

  // Mobile specific styling
  @media (max-width: 767.98px) {
    padding: 10px;

    .checkin-logo {
      max-width: 150px;
      margin: 15px auto;
    }

    .checkin-card {
      padding: 0;
    }

    .checkin-title {
      font-size: 1.3rem;
    }

    .property-name {
      font-size: 1.1rem;
    }

    .section-title {
      font-size: 1.1rem;
    }

    .section-card {
      padding: 0;
    }

    .responsive-datepicker {
      width: 100%;
    }
  }
}

// Override some Bootstrap Vue and v-select styles for better mobile experience
.v-select {
  .vs__dropdown-toggle {
    padding: 6px 0;
  }
}

// Improve form inputs on small screens
@media (max-width: 767.98px) {

  .form-group label {
    font-size: 14px;
  }
}
</style>
